import { apiFetch } from "../../utils/apiFetch";
import type {
  TicketAnzahl, TicketBereich, TicketDetail, TicketEntwurf, TicketListenZeile, TicketPrio, TicketReiter, TicketStatus,
} from "./ticketTypen";

export type AnhangEntwurf = { datei: Blob; name: string; art: "screenshot" | "datei" };

export type ListenParams = {
  status?: TicketReiter; art?: string; bereich?: string; prio?: string;
  nurMeine?: boolean; tenant?: string; q?: string; sort?: "aktivitaet" | "prio";
};

const formular = (felder: Record<string, string>, anhaenge: AnhangEntwurf[]) => {
  const fd = new FormData();
  Object.entries(felder).forEach(([k, v]) => fd.append(k, v));
  fd.append("arten", JSON.stringify(anhaenge.map((a) => a.art)));
  anhaenge.forEach((a) => fd.append("dateien", a.datei, a.name));
  return fd;
};

const json = (body: unknown): RequestInit => ({
  method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
});

export const ticketService = {
  async liste(p: ListenParams) {
    const params: Record<string, string> = {};
    if (p.status) params.status = p.status;
    if (p.art) params.art = p.art;
    if (p.bereich) params.bereich = p.bereich;
    if (p.prio) params.prio = p.prio;
    if (p.nurMeine) params.nur_meine = "1";
    if (p.tenant) params.tenant = p.tenant;
    if (p.q) params.q = p.q;
    if (p.sort) params.sort = p.sort;
    const { data } = await apiFetch<{ tickets: TicketListenZeile[]; anzahl: TicketAnzahl }>("tickets", { params });
    return data;
  },
  async detail(id: number) {
    return (await apiFetch<TicketDetail>(`tickets/${id}`)).data;
  },
  async anlegen(entwurf: TicketEntwurf, anhaenge: AnhangEntwurf[]) {
    const body = formular({ daten: JSON.stringify(entwurf) }, anhaenge);
    return (await apiFetch<{ id: number }>("tickets", { method: "POST", body })).data;
  },
  async eintrag(id: number, art: "kommentar" | "ergaenzung", text: string, anhaenge: AnhangEntwurf[]) {
    const body = formular({ art, text }, anhaenge);
    return (await apiFetch(`tickets/${id}/eintraege`, { method: "POST", body })).data;
  },
  async status(id: number, status: TicketStatus, begruendung?: string) {
    return (await apiFetch(`tickets/${id}/status`, json({ status, begruendung }))).data;
  },
  /** Ins Archiv legen bzw. zurückholen — der Status bleibt. */
  async archiv(id: number, archiviert: boolean) {
    return (await apiFetch(`tickets/${id}/archiv`, json({ archiviert }))).data;
  },
  async prio(id: number, prio: TicketPrio | null) {
    return (await apiFetch(`tickets/${id}/prio`, json({ prio }))).data;
  },
  async bereich(id: number, bereich: TicketBereich) {
    return (await apiFetch(`tickets/${id}/bereich`, json({ bereich }))).data;
  },
  /** API-relativer Pfad für useBlobUrl / Download. */
  anhangPfad: (id: number, anhangId: number) => `tickets/${id}/anhaenge/${anhangId}`,
};
